import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { trainingAnalysisAPI } from '../../services/api'
import ExerciseProgressionChart from './ExerciseProgressionChart'
import VariantSuggestionPrompt from './VariantSuggestionPrompt'
import ManualVariantLink from './ManualVariantLink'
import TrainingLinksSection from './TrainingLinksSection'

const RANGES = ['4w', '3m', '6m', '12m', 'all']
const fmt = (v) => (v == null ? '—' : Math.round(v * 10) / 10)

/** One chart per movement (its linked variants together), one per unlinked exercise. */
function groupProgressions(exercises, movements) {
  const byName = Object.fromEntries(exercises.map((e) => [e.exercise_name, e]))
  const used = new Set()
  const groups = []
  movements.forEach((m) => {
    const members = m.variants.map((v) => byName[v]).filter(Boolean)
    if (members.length === 0) return
    members.forEach((e) => used.add(e.exercise_name))
    groups.push({ key: `m-${m.movement_name}`, title: m.movement_name, exercises: members, switches: m.switches })
  })
  exercises.filter((e) => !used.has(e.exercise_name))
    .forEach((e) => groups.push({ key: `e-${e.exercise_name}`, title: e.exercise_name, exercises: [e], switches: [] }))
  return groups
}

function SectionFailed({ onRetry }) {
  const { t } = useTranslation()
  return (
    <div className="border border-red-300 rounded-lg p-3 text-sm" role="alert" data-testid="section-failed">
      <p>{t('trainingAnalysis.sectionFailed')}</p>
      <button type="button" onClick={onRetry} className="mt-1 underline">{t('trainingAnalysis.retry')}</button>
    </div>
  )
}

function WorkPerSession({ work }) {
  const { t } = useTranslation()
  const spread = work.spread_thinner
  return (
    <section className="space-y-2" data-testid="work-per-session">
      <h3 className="text-base font-semibold">{t('trainingAnalysis.work.title')}</h3>
      {spread.applies && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm" data-testid="more-but-lighter">
          {t('trainingAnalysis.work.moreButLighter', {
            from: spread.first_month, to: spread.last_month,
            sessionsFirst: spread.sessions_first, sessionsLast: spread.sessions_last,
            setsFirst: fmt(spread.sets_per_session_first), setsLast: fmt(spread.sets_per_session_last),
          })}
        </div>
      )}
      {!work.sufficient_data ? (
        <p className="text-sm text-gray-600" data-testid="not-enough-data">{t('trainingAnalysis.work.notEnoughData', { months: work.months_required })}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-gray-600">
                <th className="py-1 pr-3">{t('trainingAnalysis.work.month')}</th>
                <th className="py-1 pr-3">{t('trainingAnalysis.work.sessions')}</th>
                <th className="py-1 pr-3">{t('trainingAnalysis.work.strengthShare')}</th>
                <th className="py-1 pr-3">{t('trainingAnalysis.work.setsPerSession')}</th>
                <th className="py-1 pr-3">{t('trainingAnalysis.work.volumePerSession')}</th>
              </tr>
            </thead>
            <tbody>
              {work.months.map((m) => (
                <tr key={m.month} className="border-t">
                  <td className="py-1 pr-3">{m.month}</td>
                  <td className="py-1 pr-3">{m.sessions}</td>
                  <td className="py-1 pr-3">{m.strength_share_pct == null ? '—' : `${Math.round(m.strength_share_pct)} %`}</td>
                  <td className="py-1 pr-3">{fmt(m.working_sets_per_session)}</td>
                  <td className="py-1 pr-3">{m.volume_per_session_kg == null ? '—' : `${Math.round(m.volume_per_session_kg)} kg`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function StrengthVsMuscle({ body }) {
  const { t } = useTranslation()
  if (!body.available || !body.strength_without_muscle) return null
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm" data-testid="strength-without-muscle">
      <p className="font-medium">{t('trainingAnalysis.body.title')}</p>
      <p>{t('trainingAnalysis.body.text', {
        exercises: body.progressing_main_exercises.join(', '),
        from: body.first_date, to: body.last_date, change: fmt(body.muscle_mass_change_kg),
      })}</p>
      <ul className="list-disc ml-5 mt-1">
        {body.look_at.map((k) => <li key={k}>{t(`trainingAnalysis.body.lookAt.${k}`)}</li>)}
      </ul>
      <Link to="/nutrition" className="underline mt-1 inline-block">{t('trainingAnalysis.body.nutritionLink')}</Link>
    </div>
  )
}

/** VH-225: training analysis. Self-contained so it can become a Health Cycle tab. */
export default function TrainingAnalysisTab({ defaultRange = '6m' }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [range, setRange] = useState(defaultRange)

  const query = useQuery({
    queryKey: ['training-analysis', range],
    queryFn: () => trainingAnalysisAPI.getAnalysis(range).then((r) => r.data),
  })
  const suggestions = useQuery({
    queryKey: ['training-analysis', 'variant-suggestions'],
    queryFn: () => trainingAnalysisAPI.getVariantSuggestions().then((r) => r.data),
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['training-analysis'] })
  const link = useMutation({ mutationFn: (payload) => trainingAnalysisAPI.createVariantLink(payload), onSuccess: invalidate })
  const reject = useMutation({ mutationFn: (id) => trainingAnalysisAPI.rejectVariantLink(id), onSuccess: invalidate })

  const data = query.data
  const failed = new Set(data?.failed_sections || [])
  const pending = (suggestions.data || []).filter((s) => s.status === 'suggested')
  const busy = link.isPending || reject.isPending
  const confirm = (s) => link.mutate({
    from_exercise_name: s.from_exercise_name, to_exercise_name: s.to_exercise_name,
    movement_name: s.movement_name, ...(s.switch_date ? { switch_date: s.switch_date } : {}),
  })

  return (
    <section className="space-y-4" data-testid="training-analysis-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('trainingAnalysis.title')}</h2>
        <div className="flex gap-1" role="group" aria-label={t('trainingAnalysis.rangeLabel')}>
          {RANGES.map((r) => (
            <button key={r} type="button" onClick={() => setRange(r)} aria-pressed={range === r}
              className={`px-3 py-1 text-sm rounded ${range === r ? 'bg-primary text-white' : 'border'}`}>
              {t(`trainingAnalysis.ranges.${r}`)}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading && <div className="h-48 bg-surface-container border rounded-lg animate-pulse" />}
      {query.isError && <SectionFailed onRetry={() => query.refetch()} />}
      {(link.isError || reject.isError) && (
        <p className="text-sm text-red-600" role="alert">{t('trainingAnalysis.variants.actionError')}</p>
      )}

      {pending.map((s) => (
        <VariantSuggestionPrompt key={s.id} suggestion={s} busy={busy} onConfirm={confirm} onReject={(x) => reject.mutate(x.id)} />
      ))}

      {data && (
        <>
          {!data.sufficient_data && (
            <p className="text-sm text-gray-600" data-testid="insufficient-data">
              {t('trainingAnalysis.insufficient', { sessions: data.session_count, min: data.min_sessions_per_exercise })}
            </p>
          )}

          {data.rpe && !data.rpe.intensity_judgeable && (
            <div className="border border-blue-300 bg-blue-50 rounded-lg p-3 text-sm" data-testid="rpe-gap">
              {t('trainingAnalysis.rpe.gap', { pct: Math.round(data.rpe.coverage_pct ?? 0), threshold: data.rpe.threshold_pct })}
            </div>
          )}

          {data.body_composition && <StrengthVsMuscle body={data.body_composition} />}

          <section className="space-y-3">
            <h3 className="text-base font-semibold">{t('trainingAnalysis.progression.title')}</h3>
            {failed.has('progression') ? <SectionFailed onRetry={() => query.refetch()} /> : data.exercises.length === 0 ? (
              <p className="text-sm text-gray-600">{t('trainingAnalysis.progression.none')}</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {groupProgressions(data.exercises, data.movements).map((g) => (
                  <ExerciseProgressionChart key={g.key} title={g.title} exercises={g.exercises} switches={g.switches} />
                ))}
              </div>
            )}
            {data.exercises.length > 1 && (
              <ManualVariantLink exerciseNames={data.exercises.map((e) => e.exercise_name)} busy={busy} onLink={(p) => link.mutate(p)} />
            )}
          </section>

          {data.work_per_session
            ? <WorkPerSession work={data.work_per_session} />
            : failed.has('work_per_session') && <SectionFailed onRetry={() => query.refetch()} />}

          {data.links
            ? <TrainingLinksSection links={data.links} />
            : failed.has('links') && <SectionFailed onRetry={() => query.refetch()} />}
        </>
      )}
    </section>
  )
}
